<template>
    <master-layout>

        <!-- Breadcamb -->
        <div class="breadcamb_box">
            <div>
                <h4 class="page-title"><i class="ti-bar-chart"></i> {{ $t('loss_reasons_report') }}</h4>
            </div>
            <pml-back-button></pml-back-button>
        </div>

        <!-- Filter Loss Reasons -->
        <div class="card-box">
            <form @submit.prevent="doFilter()">
                <div class="row">
                    <div class="col-xs-12 col-md-6 m-b-15 clearfix" :class="{'has-error': errors['created_at_from'], 'has-error': errors['created_at_to']}">
                        <label>{{ $t('created_at') }}</label>
                        <div class="input-daterange input-group">
                            <pml-date-picker v-model="filter.created_at_from"></pml-date-picker>
                            <span class="input-group-addon b-0"><i class="fa fa-calendar"></i></span>
                            <pml-date-picker v-model="filter.created_at_to"></pml-date-picker>
                        </div>
                        <span class="error" v-if="errors['created_at_from']">{{ errors['created_at_from'][0] }}</span>
                        <span class="error" v-if="errors['created_at_to']">{{ errors['created_at_to'][0] }}</span>
                    </div>
                    <div class="col-xs-12 col-md-6 m-b-15 clearfix" :class="{'has-error': errors['closed_at_from'], 'has-error': errors['closed_at_to']}">
                        <label>{{ $t('lost_at') }}</label>
                        <div class="input-daterange input-group">
                            <pml-date-picker v-model="filter.closed_at_from"></pml-date-picker>
                            <span class="input-group-addon b-0"><i class="fa fa-calendar"></i></span>
                            <pml-date-picker v-model="filter.closed_at_to"></pml-date-picker>
                        </div>
                        <span class="error" v-if="errors['closed_at_from']">{{ errors['closed_at_from'][0] }}</span>
                        <span class="error" v-if="errors['closed_at_to']">{{ errors['closed_at_to'][0] }}</span>
                    </div>
                    <div class="col-xs-12 col-md-4 m-b-15 clearfix" :class="{'has-error': errors['member_id']}">
                        <label>{{ $t('consultant') }}</label>
                        <pml-select-members v-model="filter.member_id" :onlyActive="true"></pml-select-members>
                        <span class="error" v-if="errors['member_id']">{{ errors['member_id'][0] }}</span>
                    </div>
                    <div class="col-xs-12 col-md-4 m-b-15 clearfix" :class="{'has-error': errors['source_id']}">
                        <label>{{ $t('source') }}</label>
                        <pml-select v-model="filter.source_id" :options="sources"></pml-select>
                        <span class="error" v-if="errors['source_id']">{{ errors['source_id'][0] }}</span>
                    </div>
                    <div class="col-xs-12 col-md-4 m-b-15 clearfix" :class="{'has-error': errors['source_id']}">
                        <label>{{ $t('segments') }}</label>
                        <pml-select v-model="filter.segment_id" :options="segments"></pml-select>
                        <span class="error" v-if="errors['source_id']">{{ errors['source_id'][0] }}</span>
                    </div>
                </div>
                <hr class="m-t-0">
                <div class="clearfix">
                    <span class="btn btn-white pull-left" @click.prevent="clear"><i class="fa fa-paint-brush m-r-5"></i> {{ $t('clear') }}</span>
                    <button class="btn btn_green pull-right" type="submit" v-pml-block="isLoading">
                        <span class="btn-label">
                            <i class="fa fa-search"></i>
                        </span>
                        {{ $t('filter') }}
                    </button>
                </div>
            </form>
        </div>

        <!-- Graphics Loss Reasons -->
        <div class="row">
            <div class="col-xs-12 col-sm-6">
                <div class="card-box">
                    <h4 class="header-title m-t-0 m-b-20 text-dark">{{ $t('by_members') }}</h4>
                    <div ref="chartByMembers" v-show="hasMemberRegistry"></div>
                    <pml-ec-pizza v-if="!hasMemberRegistry"></pml-ec-pizza>
                </div>
            </div>
            <div class="col-xs-12 col-sm-6">
                <div class="card-box">
                    <h4 class="header-title m-t-0 m-b-20 text-dark">{{ $t('by_reasons') }}</h4>
                    <div ref="chartByReasons" v-show="hasReasonRegistry"></div>
                    <pml-ec-pizza v-if="!hasReasonRegistry"></pml-ec-pizza>
                </div>
            </div>
        </div>

        <!-- Result Loss Reasons -->
        <List :records="results" v-pml-block="isLoading" />
    </master-layout>
</template>

<script>
    import c3 from 'c3'
    import * as d3 from 'd3'
    import moment from 'moment'
    import { getLossReasonsReport } from '@/services/reports'
    import { mapGetters } from 'vuex'
    import { groupBy, find } from 'lodash'
    import { trans } from '@/utils/helpers/translation'
    import List from '@/modules/reports/loss-reasons/components/List'

    export default {
        name: 'LossReasonsReport',
        components: { List },
        data () {
            return {
                results: false,
                chartByMembers: [],
                chartByReasons: [],
                filter: {
                    created_at_from: null,
                    created_at_to: null,
                    closed_at_from: null,
                    closed_at_to: null,
                    member_id: null,
                    segment_id: null,
                    source_id: null
                },
                isLoading: false,
                errors: []
            }
        },
        metaInfo () {
            return {
                title: trans('loss_reasons_report')
            }
        },
        computed: {
            ...mapGetters({
                'sources': 'getSources',
                'segments': 'getSegments'
            }),
            members () {
                return groupBy(this.results, 'member_id')
            },
            lossReasons () {
                return groupBy(this.results, 'loss_reason_id')
            },
            hasMemberRegistry () {
                return this.chartByMembers.length !== 0
            },
            hasReasonRegistry () {
                return this.chartByReasons.length !== 0
            }
        },
        mounted () {
            this.clear()
            this.doFilter()
        },
        methods: {
            clear () {
                this.filter.source_id = null
                this.filter.segment_id = null
                this.filter.member_id = null
                this.filter.created_at_from = moment().subtract(14, 'days').format('DD/MM/YYYY')
                this.filter.created_at_to = moment().add(1, 'days').format('DD/MM/YYYY')
                this.filter.closed_at_from = moment().subtract(14, 'days').format('DD/MM/YYYY')
                this.filter.closed_at_to = moment().add(1, 'days').format('DD/MM/YYYY')
                this.errors = []
            },
            doFilter () {
                this.isLoading = true

                getLossReasonsReport(this.filter)
                    .then(response => {
                        this.results = response.data.base
                        this.chartByMembers = response.data.by_members.map(item => {
                            return [
                                this.$options.filters.pmlMemberById(item.member_id),
                                item.amount
                            ]
                        })
                        this.chartByReasons = response.data.by_reasons.map(item => {
                            return [
                                this.$options.filters.pmlLossReasonById(item.loss_reason_id),
                                item.amount
                            ]
                        })
                        this.errors = []
                        this.buildChart()
                    })
                    .catch(error => {
                        this.errors = error.response.data.errors || []
                    })
                    .then(() => {
                        this.isLoading = false
                    })
            },
            getAmount (memberId, lossReasonId) {
                const obj = find(this.results, { 'member_id': parseInt(memberId), 'loss_reason_id': parseInt(lossReasonId) })
                return obj ? obj.amount : 0
            },
            buildChart () {
                c3.generate({
                    bindto: this.$refs.chartByMembers,
                    data: {
                        columns: this.chartByMembers,
                        type: 'pie'
                    },
                    legend: {
                        position: 'right'
                    },
                    pie: {
                        label: {
                            show: false
                        }
                    },
                    tooltip: {
                        format: {
                            value (value, ratio, id, index) {
                                return `&nbsp;${d3.format('.1%')(ratio)} (${value})`
                            }
                        }
                    }
                })

                c3.generate({
                    bindto: this.$refs.chartByReasons,
                    data: {
                        columns: this.chartByReasons,
                        type: 'pie'
                    },
                    pie: {
                        label: {
                            show: false
                        }
                    },
                    legend: {
                        position: 'right'
                    },
                    tooltip: {
                        format: {
                            value (value, ratio, id, index) {
                                return `&nbsp;${d3.format('.1%')(ratio)} (${value})`
                            }
                        }
                    }
                })
            }
        }
    }
</script>



// WEBPACK FOOTER //
// src/modules/reports/loss-reasons/components/Main.vue